import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useNavigate, Link } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { CartProvider } from '@/contexts/CartContext';
import { Toaster } from '@/components/ui/sonner';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { initAnalytics, trackPageView, trackInitialPageView } from '@/lib/analytics';
import '@/App.css';

// Layouts
import PublicLayout from '@/layouts/PublicLayout';
import AccountLayout from '@/layouts/AccountLayout';
import TeamLayout from '@/layouts/TeamLayout';

// Public pages
import HomePage from '@/pages/public/HomePage';
import CMSPage from '@/pages/public/CMSPage';
import ServicesPage from '@/pages/public/ServicesPage';
import BrandsPage from '@/pages/public/BrandsPage';
import BrandDetailPage from '@/pages/public/BrandDetailPage';
import CategoryPage from '@/pages/public/CategoryPage';
import VerifyPage from '@/pages/public/VerifyPage';

// Order pages
import ItemSubmitPage from '@/pages/order/ItemSubmitPage';
import CartPage from '@/pages/order/CartPage';
import CheckoutPage from '@/pages/order/CheckoutPage';
import ConfirmationPage from '@/pages/order/ConfirmationPage';
import PaymentStatusPage from '@/pages/order/PaymentStatusPage';

// Account pages
import SignInPage from '@/pages/account/SignInPage';
import MagicLinkPage from '@/pages/account/MagicLinkPage';
import ForgotPasswordPage from '@/pages/account/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/account/ResetPasswordPage';
import DashboardPage from '@/pages/account/DashboardPage';
import OrdersPage from '@/pages/account/OrdersPage';
import OrderDetailPage from '@/pages/account/OrderDetailPage';
import DocumentsPage from '@/pages/account/DocumentsPage';
import AddressesPage from '@/pages/account/AddressesPage';
import AccountSettingsPage from '@/pages/account/AccountSettingsPage';
import GoogleAuthCallback from '@/pages/account/GoogleAuthCallback';

// Team pages
import QueuePage from '@/pages/team/QueuePage';
import CompletedPage from '@/pages/team/CompletedPage';
import ItemDetailPage from '@/pages/team/ItemDetailPage';

// Admin pages
import CMSEditorPage from '@/pages/admin/CMSEditorPage';
import ReportsPage from '@/pages/admin/ReportsPage';
import CouponsPage from '@/pages/admin/CouponsPage';
import EmailTemplatesPage from '@/pages/admin/EmailTemplatesPage';
import DocumentTemplatesPage from '@/pages/admin/DocumentTemplatesPage';
import SettingsPage from '@/pages/admin/SettingsPage';
import UsersPage from '@/pages/admin/UsersPage';
import UserDetailPage from '@/pages/admin/UserDetailPage';
import BrandPagesPage from '@/pages/admin/BrandPagesPage';
import OfflineCertsPage from '@/pages/admin/OfflineCertsPage';
import ImageRulesPage from '@/pages/admin/ImageRulesPage';
import BrandsAdminPage from '@/pages/admin/BrandsAdminPage';
import CategoriesAdminPage from '@/pages/admin/CategoriesAdminPage';
import PendingOrdersPage from '@/pages/admin/PendingOrdersPage';
import PerfDashboardPage from '@/pages/admin/PerfDashboardPage';
import ExportsPage from '@/pages/admin/ExportsPage';
import ImportDashboardPage from '@/pages/admin/ImportDashboardPage';
import LaunchReadinessPage from '@/pages/admin/LaunchReadinessPage';
import EmailDiagnosticsPage from '@/pages/admin/EmailDiagnosticsPage';
import PrivacyScrubPage from '@/pages/admin/PrivacyScrubPage';

function App() {
  // Initialize analytics from backend config on mount.
  // ANL-02: AnalyticsListener fires the FIRST page_view on mount, but that runs
  // before this async /config fetch resolves — so GA4/Meta (loaded inside
  // initAnalytics) miss the landing-page view. After init, replay the current
  // page to the third-party pixels (first-party beacon already fired, so it's
  // suppressed to avoid double-counting).
  useEffect(() => {
    api.get('/config').then(r => {
      initAnalytics(r.data);
      trackInitialPageView(window.location.pathname + window.location.search);
    }).catch(() => {});
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AnalyticsListener />
          <ScrollToTop />
          <AuthCallbackGate>
          <Routes>
            {/* Public routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/authentication" element={<CMSPage pageSlug="authentication" />} />
              <Route path="/services/appraisal" element={<CMSPage pageSlug="appraisal" />} />
              <Route path="/brands" element={<BrandsPage />} />
              <Route path="/brands/:slug" element={<BrandDetailPage />} />
              <Route path="/categories/:categoryId" element={<CategoryPage />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/verify/:code" element={<VerifyPage />} />
              <Route path="/coa" element={<LegacyVerifyRedirect />} />
              <Route path="/coa/:code" element={<LegacyVerifyRedirect />} />
              <Route path="/verify-authentication" element={<LegacyVerifyRedirect />} />
              <Route path="/verify-authentication/:code" element={<LegacyVerifyRedirect />} />
              <Route path="/about" element={<CMSPage pageSlug="about" />} />
              <Route path="/faq" element={<CMSPage pageSlug="faq" />} />
              <Route path="/terms" element={<CMSPage pageSlug="terms" />} />
              <Route path="/privacy" element={<CMSPage pageSlug="privacy" />} />
              <Route path="/acceptable-use" element={<CMSPage pageSlug="acceptable-use" />} />
              <Route path="/contact" element={<CMSPage pageSlug="contact" />} />
              {/* Order flow */}
              <Route path="/order/submit" element={<ItemSubmitPage />} />
              <Route path="/order/cart" element={<CartPage />} />
              <Route path="/order/checkout" element={<CheckoutPage />} />
              <Route path="/order/payment-status" element={<PaymentStatusPage />} />
              <Route path="/order/confirmation/:orderId" element={<ConfirmationPage />} />
            </Route>

            {/* Auth routes */}
            <Route path="/account/sign-in" element={<SignInPage />} />
            {/* BUG-04: common aliases users try — redirect to the canonical /account/sign-in. */}
            <Route path="/signin" element={<Navigate to="/account/sign-in" replace />} />
            <Route path="/account/signin" element={<Navigate to="/account/sign-in" replace />} />
            <Route path="/login" element={<Navigate to="/account/sign-in" replace />} />
            <Route path="/account/magic" element={<MagicLinkPage />} />
            <Route path="/account/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/account/reset-password" element={<ResetPasswordPage />} />

            {/* Account routes */}
            <Route path="/account" element={<AccountLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/:orderId" element={<OrderDetailPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="addresses" element={<AddressesPage />} />
              <Route path="settings" element={<AccountSettingsPage />} />
            </Route>

            {/* Team/Admin routes */}
            <Route path="/team" element={<TeamLayout />}>
              <Route index element={<QueuePage />} />
              <Route path="completed" element={<CompletedPage />} />
              <Route path="items/:itemId" element={<ItemDetailPage />} />
              <Route path="cms" element={<CMSEditorPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="coupons" element={<CouponsPage />} />
              <Route path="emails" element={<EmailTemplatesPage />} />
              <Route path="documents" element={<DocumentTemplatesPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:userId" element={<UserDetailPage />} />
              <Route path="brand-pages" element={<BrandPagesPage />} />
              <Route path="offline-certs" element={<OfflineCertsPage />} />
              <Route path="image-rules" element={<ImageRulesPage />} />
              <Route path="brands" element={<BrandsAdminPage />} />
              <Route path="categories" element={<CategoriesAdminPage />} />
              <Route path="imports" element={<ImportDashboardPage />} />
              <Route path="launch" element={<LaunchReadinessPage />} />
              <Route path="email-diagnostics" element={<EmailDiagnosticsPage />} />
              <Route path="privacy" element={<PrivacyScrubPage />} />
              <Route path="orders/pending" element={<PendingOrdersPage />} />
              <Route path="exports" element={<ExportsPage />} />
              <Route path="debug/perf" element={<PerfDashboardPage />} />
            </Route>

            {/* Catch-all: legacy redirect lookup, else a branded 404 (RTE-01) */}
            <Route path="*" element={<NotFoundRedirect />} />
          </Routes>
          </AuthCallbackGate>
          <Toaster />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Detect the one-time #session_id fragment from Emergent Google auth
// synchronously during render — BEFORE any route/guard runs — so the exchange
// always wins the race against the bootstrap /auth/me check.
function AuthCallbackGate({ children }) {
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }
  return children;
}

function LegacyVerifyRedirect() {
  const location = useLocation();
  const { code } = useParams();
  const params = new URLSearchParams(location.search);

  if (code && !params.get('code')) {
    params.set('code', code);
  }

  const target = params.toString() ? `/verify?${params.toString()}` : '/verify';
  return <Navigate to={target} replace />;
}

// Fire a first-party + GA4/Meta page-view on every navigation. GA4 auto
// page-views are disabled (analytics.js sets send_page_view:false), so this is
// the single source of route-change tracking (ANL-01).
function AnalyticsListener() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// Reset scroll to the top on every route change so new screens open at the top
// (previously pages could open mid-scroll, misaligned with the header/buttons).
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Catch-all (RTE-01): consult the legacy redirect map for the current path; on a
// hit, navigate/replace to the mapped target (honoring its status_code intent);
// on a miss, render a branded 404. Never leaves a blank screen.
function NotFoundRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let active = true;
    setResolved(false);
    api.get(`/redirects/lookup?path=${encodeURIComponent(location.pathname)}`)
      .then(r => {
        if (!active) return;
        if (r.data?.found && r.data?.target) {
          navigate(r.data.target, { replace: true });
        } else {
          setResolved(true);
        }
      })
      .catch(() => { if (active) setResolved(true); });
    return () => { active = false; };
  }, [location.pathname, navigate]);

  if (!resolved) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="notfound-resolving">
        <div className="h-6 w-6 rounded-full border-2 border-stone border-t-transparent animate-spin" />
      </div>
    );
  }
  return <NotFoundPage />;
}

function NotFoundPage() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="notfound-page">
      <div className="text-center max-w-md">
        <p className="font-display text-5xl font-medium text-ink mb-2">404</p>
        <h1 className="text-lg font-semibold text-ink mb-2">Page not found</h1>
        <p className="text-sm text-muted-foreground mb-6">
          The page you&apos;re looking for doesn&apos;t exist or may have moved.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to="/" className="text-sm font-medium underline" data-testid="notfound-home-link">Home</Link>
          <Link to="/services" className="text-sm font-medium underline" data-testid="notfound-services-link">Services</Link>
          <Link to="/brands" className="text-sm font-medium underline" data-testid="notfound-brands-link">Brands</Link>
          <Link to="/verify" className="text-sm font-medium underline" data-testid="notfound-verify-link">Verify a Certificate</Link>
        </div>
      </div>
    </div>
  );
}

export default App;
